import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Flex } from "antd";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useCreateDirection } from "~/entity/Directions";
import { useUpdateDirection } from "~/entity/Directions/hooks/useUpdateDirection";
import { useGetLocationData } from "~/hooks/locationData";
import { OpenModal } from "~/pages/Editor/type";
import { useNotify } from "~/shared/hooks/useNotify";
import { FormStatus } from "~/shared/lib/statuses";
import { Field } from "~/shared/ui/Field";

import { schema } from "./validation";

export type FormSubmit = {
  name: string;
  status: { label: string; value: string };
};

type Props = {
  closeModal: () => void;
  refetch: () => void;
  currentData: any[];
  formId: string;
};

const DirectionsForm = ({
  closeModal,
  refetch,
  currentData,
  formId,
}: Props) => {
  const { t } = useTranslation();
  const { notifySuccess, notifyError } = useNotify();
  const { currentId, openModal } = useGetLocationData();
  const isEdit = openModal === OpenModal.Edit;
  const { create } = useCreateDirection();
  const { update } = useUpdateDirection();

  const statusOptions = [
    { value: "active", label: t("directions.active") },
    { value: "inactive", label: t("directions.inactive") },
  ];

  const currentItem = isEdit
    ? currentData?.find((item) => item.id === Number(currentId))
    : undefined;

  const { handleSubmit, control, errors } = useForm({
    defaultValues: {
      name: currentItem?.name ?? "",
      status: statusOptions.find(({ value }) => value === currentItem?.status),
    },
    resolver: yupResolver(schema),
  });

  const onSuccessHandler = async () => {
    await refetch();
    closeModal();
    notifySuccess(t("common.сhange_was_successful"));
  };

  const onErrorHandler = (error: any) => {
    notifyError(error?.message || "Ошибка выполнения запроса");
  };

  const submit: SubmitHandler<FormSubmit> = (data) => {
    const payload = {
      name: data.name,
      status: FormStatus[data.status.value],
    };

    if (isEdit && currentItem) {
      update(
        {
          instanceId: currentItem.instanceId,
          ...payload,
        },
        { onSuccess: onSuccessHandler, onError: onErrorHandler },
      );
    } else if (openModal === OpenModal.Add) {
      create(payload, {
        onSuccess: onSuccessHandler,
        onError: onErrorHandler,
      });
    }
  };

  return (
    <form id={formId} onSubmit={handleSubmit(submit)}>
      <Flex vertical gap={10}>
        <Field
          type="input"
          name="name"
          control={control}
          label={t("directions.name")}
          error={errors?.name?.message}
        />
        <Field
          type="select"
          name="status"
          control={control}
          label={t("directions.status")}
          options={statusOptions}
          labelInValue
          error={(errors?.status as unknown as { message?: string })?.message}
        />
      </Flex>
    </form>
  );
};

export default DirectionsForm;
